// Generated by scripts/build-rules-content.mjs from country-in.json (aligned 9 Oct 2026).
// Content for sales: plain sentences, no em dash, no person or company names.
// Edit here or regenerate; tests/rules-explainer.test.mjs checks the writing rules.
import type { CountryGuide } from "../types";

export const GUIDE_INDIA: CountryGuide = {
  id: "india",
  code: "in",
  title: "India (DISA India)",
  shortName: "India",
  entities: ["in"],
  areaRoles: ["in"],
  live: true,
  cardLine:
    "Mostly distributors, prices only from the India price list, freight in the price, and an HSN code on every line.",
  summary:
    "India works differently from the rest of Norican in several ways. Most inquiries come from distributors, prices come only from the India price list (no central base price), freight is already in the price, every line needs an HSN code and GST depends on where the customer is invoiced. This guide lists what Norican I2Q checks for India today, what is planned, and what you do in each case. You always decide and send the quotation yourself.",
  howInquiriesArrive: [
    "Inquiries come to the shared mailbox Disa.AM.India (it replaced Disa.orders.india in July 2025). Norican I2Q reads a copy of that mailbox, so nothing changes for you in Outlook and nothing is sent from I2Q. Quotes and orders together are almost all of the mail in this box. Only price and quotation requests become drafts; purchase orders are counted but parked. About 70 % of requests come from distributors (5 distributors at about 9 locations), the rest from 90 to 95 direct customers. Distributors often write from free mail addresses such as gmail, and a mail often contains a whole thread or a forwarded end customer mail.",
  ],
  specialities: [
    {
      id: "in-distributors",
      title: "Distributors: quote the distributor first, show the end customer",
      status: "active",
      tone: "info",
      topic: "customer",
      checks:
        "Which account the quotation is for when a distributor writes on behalf of an end customer.",
      why: "A distributor writes for many end customers. The quotations of India sales are mostly on the account of the distributor. The product owner decided on 14 October 2026, from that analysis and for now, that India quotes the distributor first.",
      youSee:
        'Info. The card shows both parties. The distributor carries "Quoted first", the end customer stands next to it. An account of the distributor for this end customer comes first. This is always a suggestion. When the distributor has no account, the open point says "I2Q found no account in F&O for the agent.".',
      whatToDo:
        'Check that the distributor is the one in the mail. Make the quotation on the account with "Quoted first" and send it to the distributor. When two accounts carry the name, pick the one with the right town and postcode.',
      example:
        "A distributor in Pune forwards a mail from a foundry that needs 20 shot blast wheel blades. I2Q puts the distributor's account first and shows the foundry next to it. You confirm the distributor, quote on that account and reply to the distributor.",
      registryIds: [],
      glossaryIds: ["agents-and-distributors"],
      source:
        "goa-knowledge.md 2.1 and Part B IN; eligibility-rules-concept.md (India row); transcript Go A India testing intro 14th July [00:08]-[00:16]",
    },
    {
      id: "in-free-mail-sender",
      title: "Free mail senders",
      status: "active",
      tone: "info",
      topic: "customer",
      checks: "Whether the sender writes from a free mail address such as gmail.",
      why: "About a third of the Indian mails where Go Autonomous found no customer came from free mail addresses.",
      youSee:
        "The customer is not taken from the mail domain. You see a suggestion from the signature or address, or no customer at all.",
      whatToDo:
        "Check the signature, the company name and the address in the mail and confirm or pick the customer.",
      example:
        'A mail from a gmail address signed "Purchase dept., (distributor name), Chennai". Pick the distributor\'s Chennai account.',
      registryIds: [],
      glossaryIds: ["freemail-senders"],
      source:
        "goa-knowledge.md 2.1 (freemail); transcript Go A India testing intro 14th July [00:46]-[00:47]",
    },
    {
      id: "in-latest-message-only",
      title: "Only the newest message counts",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks: "Which part of a mail thread the lines are read from.",
      why: "Go Autonomous read whole threads and created lines from older messages, names and signatures.",
      youSee: "Lines come from the newest message and its attachments only.",
      whatToDo:
        'If the customer says "same as last time" or refers to an older mail, add the lines by hand.',
      example:
        'A reply says "please also quote the 4 liners from my mail below". Those 4 liners are not added automatically; add them yourself.',
      registryIds: [],
      glossaryIds: ["newest-message-only"],
      source:
        "goa-knowledge.md Part B rules (Thread), backlog M2; transcript Go A India testing intro [00:16]-[00:19]",
    },
    {
      id: "in-price-list-only",
      title: "Prices come only from the India price list",
      status: "active",
      tone: "info",
      topic: "price",
      checks:
        "Whether the item has a price in F&O for this customer (a customer price list, else the customer's price group), valid today and for the quantity.",
      why: "India has no central base price, and 60 to 70 % of distributor requests are for parts with no price list entry.",
      youSee:
        'Info: lines with a price show it. Blocked: the line shows "No price", and the prepared quotation says "No price was found for ...". The other lines still show.',
      whatToDo:
        "Get the price and type it in your quotation in F&O. India is the only area where the unit price may be changed by hand.",
      example:
        "A distributor asks for 3 items; 2 have a price, 1 has none. Ask for or work out the price of the third item before sending.",
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price"],
      modes: {
        "pricing.no_price": {
          in: "block",
        },
      },
      source:
        "goa-knowledge.md 2.3 (price source, no price), 9.1 India distributors (#79); Part B IN Price",
    },
    {
      id: "in-no-price-no-lead-time",
      title: "No price list means no lead time either",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks: "Lines that have no price list entry also have no lead time.",
      why: "In Go Autonomous a typed price without a price list gave the line no lead time, so the PDF printed no delivery date.",
      youSee:
        'Blocked: "Line 2 has no price and no lead time because no trade agreement applies." The other lines still show.',
      whatToDo:
        "Ask for a price. A zero price is never quoted. When you type the price in F&O, enter the lead time too (for example 35 days).",
      example:
        'You type a price for a cylinder liner without a price list entry. Also enter "35 days" as lead time, or the quotation shows no delivery date.',
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price"],
      modes: {
        "pricing.no_price": {
          in: "block",
        },
      },
      source: "goa-knowledge.md 9.1 (#119); Part B IN Price",
    },
    {
      id: "in-distributor-discount",
      title: "Distributor discounts",
      status: "planned",
      tone: "info",
      topic: "price",
      checks: "The discount a distributor gets, based on how much it has bought in total.",
      why: "These discounts were kept in an Excel sheet by customer type, so Go Autonomous could not apply them.",
      youSee:
        "Today: I2Q shows only discounts that are stored in the price list in F&O. Discounts from the Excel sheet do not appear. Planned: a hint with the distributor's discount level.",
      whatToDo: "Check the distributor's discount level and apply it if it is not in the price.",
      example:
        "A distributor in the top spend tier should get a higher discount, but the line shows the standard price. Apply the discount from the sheet.",
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md 2.3 (distributor discounts IN); Part B rules (Discounts)",
    },
    {
      id: "in-margin",
      title: "Margin check",
      status: "active",
      tone: "warning",
      topic: "price",
      checks:
        "Where F&O has a cost for the item, how much of the price is left after cost (the contribution ratio). It is compared with the margin limit for India.",
      why: "India's business lead said the group-wide margin limit is too high for India, so India has its own lower limit.",
      youSee:
        'Warning: "The price of line 2 is below the contribution threshold." with "Check the price before the quotation goes out." Nothing is held. If F&O has no cost for the item, the line shows "Margin: not checked (no cost in F&O)", and the prepared quotation is held.',
      whatToDo:
        "Check the price before the quotation goes out. If no cost is known, create the quotation in F&O as today.",
      example:
        "A large distributor order of wear parts is priced low to win the deal. The line shows the margin warning, and you check the price before you send.",
      registryIds: ["pricing.margin"],
      glossaryIds: ["contribution-ratio"],
      modes: {
        "pricing.margin": {
          in: "warn",
        },
      },
      source:
        "goa-knowledge.md 2.3 (contribution ratio gate), Part B rules (Margin); pricing/thresholds.py; help content.ts",
    },
    {
      id: "in-hsn",
      title: "HSN code on every line",
      status: "active",
      tone: "warning",
      topic: "tax",
      checks:
        "Whether each item has an HSN code (the Indian customs and tax code) of 4, 6 or 8 digits, and not the placeholder 6666. Only about 22,700 of about 135,000 India items have one, and about 8,300 carry 6666.",
      why: "HSN is required on every Indian line, and missing or placeholder codes caused about 3,000 blocked quotes in Go Autonomous.",
      youSee:
        'Today a note: "HSN codes are not in the item master data yet, so they are not checked." Once they are, a warning per line, for example "Line 2 has the placeholder HSN code 6666." I2Q never guesses a code.',
      whatToDo:
        "Take the HSN codes from F&O when you prepare the quotation. If a code is missing or 6666, ask the product team and never guess it.",
      example:
        "A filter bag shows HSN 6666. Do not send it like that; get the correct code entered in F&O first.",
      registryIds: ["gate.hsn"],
      glossaryIds: [],
      modes: {
        "gate.hsn": {
          in: "warn",
        },
      },
      source:
        "goa-knowledge.md 2.5 (HSN India), backlog M7; HSN Codes for IN Released Products.xlsx",
    },
    {
      id: "in-gst",
      title: "GST depends on the invoicing address",
      status: "planned",
      tone: "info",
      topic: "tax",
      checks:
        "Which GST applies: IGST 18 % when the invoice address is in another state, SGST plus CGST 9 % + 9 % when it is in the same state.",
      why: "In Go Autonomous the tax was set by hand, and the wrong GST gives the customer a wrong total.",
      youSee: "Planned: a hint with the expected GST type based on the invoice address.",
      whatToDo: "Check that the GST on the quotation matches the customer's invoicing state.",
      example:
        "A customer invoiced in Tamil Nadu buys from a supplying site in another state: IGST 18 %. A customer in the same state: 9 % SGST + 9 % CGST.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md 2.5 (India GST), Part B IN Tax; sa14-024 Sales quotation IN tax pages.pdf",
    },
    {
      id: "in-freight-local",
      title: "Freight is in the price for local customers",
      status: "active",
      tone: "info",
      topic: "freight",
      checks: "Whether a freight line is needed.",
      why: "Indian local prices already include door delivery, and in Go Autonomous the freight automation broke quote confirmation until it was switched off for India.",
      youSee:
        'Info: the preview\'s freight says "Does not apply". A note says "Freight is in the item price; air freight and export packing are extra lines."',
      whatToDo:
        "Do not add standard freight for local customers. Add an extra line only for air or express freight, for example for a breakdown.",
      example:
        "A machine is down and the customer wants the part by air. Add an air freight line; normal delivery stays in the price.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          in: "warn",
        },
      },
      source: "goa-knowledge.md 2.5 (freight elsewhere), 9.3 freight models; Issue Log #89",
    },
    {
      id: "in-export",
      title: "Export quotations: packing, port and tariff code",
      status: "planned",
      tone: "warning",
      topic: "freight",
      checks:
        "For export: ex works terms, a packing charge, a port ID and the customs tariff code.",
      why: "Export from India is always ex works with a packing charge and a mandatory tariff code, which Go Autonomous did not handle.",
      youSee:
        "Today the freight note says that export packing is an extra line. Planned: a warning when an export quotation has no packing line, no port or no tariff code.",
      whatToDo:
        "Add the packing charge, the port and the tariff code. Choose sea freight for heavy items.",
      example:
        "A customer in Indonesia asks for a heavy casting. Quote ex works, add export packing, name the port and suggest sea freight.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          in: "warn",
        },
      },
      source: "goa-knowledge.md Part B IN Freight, Tax; backlog M12, S8",
    },
    {
      id: "in-minimum-order",
      title: "Minimum order value INR 10,000",
      status: "planned",
      tone: "info",
      topic: "price",
      checks: "Whether the quotation total is below INR 10,000.",
      why: "About 6 % of Indian orders were below the minimum, and F&O has no rule for it.",
      youSee: "Planned: a hint on the draft when the total is below INR 10,000.",
      whatToDo: "Tell the customer about the minimum or suggest adding items.",
      example:
        "A request for 2 small bolts totals INR 3,200. Point out the INR 10,000 minimum in your reply.",
      registryIds: [],
      glossaryIds: ["minimum-order-value"],
      source: "goa-knowledge.md 9.1 (#26), backlog S16",
    },
    {
      id: "in-warehouse",
      title: "Warehouse 002 to 006 only",
      status: "planned",
      tone: "warning",
      topic: "items",
      checks: "Whether the item sits in one of the India warehouses 002 to 006.",
      why: "In Go Autonomous about 2,500 items sat in the wrong warehouse and blocked quotations.",
      youSee: "Planned: a warning on the line when the warehouse is not 002 to 006.",
      whatToDo: "Check where the item really is and correct the warehouse before quoting.",
      example:
        "An item shows warehouse 010. Check with the warehouse team before you promise a delivery date.",
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md 2.2 (warehouse), 9.1 (#82)",
    },
    {
      id: "in-item-status",
      title: "Items that are on hold or not for sale",
      status: "active",
      tone: "blocked",
      topic: "items",
      checks:
        "The item status in F&O. Only items with status OK are quoted without a second look. India also uses ONHOLD, NOMOVER, DEADM_SP and CREV.",
      why: "Go Autonomous quoted new or revised items without warning, and India has extra statuses for items that should not be sold as normal.",
      youSee:
        'Blocked: "Blocked for sale in F&O" on the line. Warning: "Phase-out item", with the replacement number when F&O has one. ONHOLD items are blocked. Warning for NOMOVER and DEADM_SP: "Line 2 has signal code NOMOVER, which needs a check before quoting."',
      whatToDo:
        "Check the item before quoting. Quote the successor if there is one, or ask the product owner.",
      example:
        'An item has status ONHOLD. The line shows "Blocked for sale in F&O". Ask the product owner whether it can be sold before you quote it in F&O.',
      plannedPart: "A warning for CREV.",
      registryIds: ["gate.signal_review"],
      glossaryIds: ["signal-codes"],
      modes: {
        "gate.signal_review": {
          in: "warn",
        },
      },
      source: "goa-knowledge.md 2.2 (signal codes), backlog M6; GOA Released Product Review (IN)",
    },
    {
      id: "in-successor",
      title: "Replacement items",
      status: "active",
      tone: "warning",
      topic: "items",
      checks: "Whether an item asked for is being phased out and has a successor in F&O.",
      why: "Go Autonomous sometimes went the wrong way between the old and the new item.",
      youSee:
        'Warning: "Line 3: use 654321, it replaces 123456." The line also shows "Phase-out item, replacement" when F&O marks the old item as phased out.',
      whatToDo: "Quote the successor and mention the old number so the customer recognises it.",
      example:
        'The customer asks for an old blade number; the successor is shown. Quote the successor and write "replaces (old number)".',
      plannedPart: 'A clear "replaces" note with the old number on the line.',
      registryIds: ["gate.successor"],
      glossaryIds: ["successor-items"],
      modes: {
        "gate.successor": {
          in: "warn",
        },
      },
      source: "goa-knowledge.md 2.2 (alternatives and successors)",
    },
    {
      id: "in-customer-item-numbers",
      title: "Customer item numbers",
      status: "active",
      tone: "info",
      topic: "items",
      checks: "Whether the number in the mail is the customer's own item number.",
      why: "India has about 1,900 customer item numbers stored in F&O, and many customers write their own numbers.",
      youSee:
        'A known customer number is matched to our item. An unknown one shows "Needs a check" or "Not evaluated, check by hand".',
      whatToDo:
        "Pick our item. Ask master data to store the customer's number if it comes up often.",
      example:
        'The customer writes "their part code BW-112" which is stored for them; I2Q matches it to our item number.',
      registryIds: [],
      glossaryIds: ["customer-item-numbers"],
      source: "goa-knowledge.md 2.2 (customer item numbers)",
    },
    {
      id: "in-sales-pool-per-line",
      title: "Sales pool (department) per line",
      status: "active",
      tone: "warning",
      topic: "quotation",
      checks: "Which sales pool and department each line belongs to: AMIP, AMF, AMM or AMW.",
      why: "Indian customers often mix moulding and shot blast parts in one request, so one pool for the whole quotation is wrong.",
      youSee:
        'Today one sales pool for the whole quotation, from the item group with the most lines. Item groups starting with SI are not mapped, so they give a warning: "Item group ... has no sales pool for this entity."',
      whatToDo: "Set the department per line in F&O when the quotation mixes product lines.",
      example:
        "One mail asks for moulding machine parts and shot blast parts. The two groups of lines get different pools.",
      plannedPart: "A pool per line, derived from the item group.",
      registryIds: ["header.pool"],
      glossaryIds: ["quotation-header"],
      modes: {
        "header.pool": {
          in: "warn",
        },
      },
      source:
        "goa-knowledge.md 2.4 (sales pool), Part B IN Pool; transcript IN changing sales pool at a line level",
    },
    {
      id: "in-sales-unit-reversed",
      title: "Sales taker and sales responsible are the other way round",
      status: "planned",
      tone: "info",
      topic: "quotation",
      checks: "Who appears as sales taker and sales responsible on the quotation.",
      why: "India uses one sales taker for all accounts and seven regional sales responsibles, the reverse of the rest of Norican, and Go Autonomous printed the taker as responsible.",
      youSee:
        "Today a note lists sales responsible and sales taker as not available yet. Planned: the sales unit from the customer account, with the India logic.",
      whatToDo: "Check that the regional responsible printed on the quotation is right.",
      example:
        "A customer in the west region: the regional responsible for the west must appear on the quotation, not the inside sales taker.",
      registryIds: ["header.not_available"],
      glossaryIds: ["quotation-header"],
      modes: {
        "header.not_available": {
          in: "info",
        },
      },
      source: "goa-knowledge.md 3 India, 2.4 (sales unit); Part B IN",
    },
    {
      id: "in-header-texts",
      title: "Header and closing texts, terms and conditions",
      status: "planned",
      tone: "info",
      topic: "quotation",
      checks:
        "That the standard India header text, closing text and terms and conditions are on the quotation.",
      why: "Go Autonomous dropped the lower part of the closing text, where India keeps its terms.",
      youSee: "Planned: the India texts on the prepared quotation before the PDF is made.",
      whatToDo: "Check the texts in F&O before you print the quotation.",
      example:
        "The closing text should end with the payment and validity terms. If the lower part is missing, add it.",
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md 5 (quote text), Part B IN Texts; backlog M17",
    },
    {
      id: "in-payment-validity",
      title: "Payment terms, validity and language",
      status: "planned",
      tone: "info",
      topic: "quotation",
      checks:
        'Payment terms from the customer account (typically "15 days credit"), validity 30 days, quotation in US English.',
      why: "India keeps the number of days in the description of the payment term, and the reply template gives 30 days validity.",
      youSee:
        "Today the preview shows the document language: the customer's, else English (US). The reply draft follows the language of the customer's mail. Planned: payment terms and validity in the quotation header.",
      whatToDo: 'Check the payment terms on the account and write "offer validity 30 days".',
      example:
        "A new direct customer has no payment terms on the account. Ask finance before you quote credit terms.",
      registryIds: [],
      glossaryIds: ["quotation-validity", "translation"],
      source: "goa-knowledge.md 3 India, 2.5 (validity, document language), 9.1 (payment terms)",
    },
    {
      id: "in-competitors-sanctions",
      title: "Competitors and blocked countries",
      status: "active",
      tone: "blocked",
      topic: "customer",
      checks: "Whether the sender is a competitor or from a sanctioned country.",
      why: "Norican quotes competitors higher or not at all, and a quotation into a sanctioned country is never allowed.",
      youSee:
        'Blocked: "The sender\'s domain is on the competitor list." The case is held and the queue shows this reason first. Blocked: "The sender\'s mail domain belongs to Russia, which is on the sanctions list."',
      whatToDo:
        "Quote a competitor only if your manager agrees. Do not quote a sanctioned country; pass it to your manager for an export control check.",
      example:
        "A request comes from a domain on the competitor list. Do not quote the normal price before checking with your manager.",
      registryIds: ["gate.competitor", "gate.sanctioned"],
      glossaryIds: ["competitor-domains", "sanctioned-countries"],
      modes: {
        "gate.competitor": {
          in: "block",
        },
        "gate.sanctioned": {
          in: "block",
        },
      },
      source: "goa-knowledge.md 2.1 (competitor domains, sanctioned countries)",
    },
    {
      id: "in-large-quotation",
      title: "Large quotations",
      status: "active",
      tone: "info",
      topic: "price",
      checks:
        "Whether the quotation total is above an approval limit. Limits exist for EUR and GBP only, so a quotation in INR is not checked yet.",
      why: "This was the only approval limit Go Autonomous had: larger quotations went to manual handling.",
      youSee:
        'Note on an INR quotation: "No approval limit is set for INR, so the total is not checked."',
      whatToDo: "Ask your manager whether this quotation needs an approval before sending.",
      example:
        "A full set of spares for a moulding line comes to INR 60 lakh. The case notes that no limit is set for INR, and you ask your manager.",
      plannedPart: "An approval limit for INR.",
      registryIds: ["gate.doa_limit"],
      glossaryIds: ["large-quotation"],
      modes: {
        "gate.doa_limit": {
          in: "warn",
        },
      },
      source: "goa-knowledge.md 2.3 (delegation of authority)",
    },
  ],
};
